import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { FaHome, FaLink, FaChartBar, FaUser, FaCog, FaSignOutAlt, FaCopy, FaDollarSign, FaWallet, FaMoneyBillWave, FaChessRook, FaLayerGroup, FaExternalLinkAlt, FaCheck, FaEdit } from 'react-icons/fa';
import config from '../../config';
import terms from '../../config/terminology';
import ProfileLinksManager from './ProfileLinksManager';
import MyCastleSection from './MyCastleSection';

export default function CreatorDashboard() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [balance, setBalance] = useState(null);
  const [tips, setTips] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [activeTab, setActiveTab] = useState('tips');
  const [thankYouMessage, setThankYouMessage] = useState('');
  const [editingThankYou, setEditingThankYou] = useState(false);
  const [savingThankYou, setSavingThankYou] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const avatarInputRef = useRef(null);
  const navigate = useNavigate();

  const DEFAULT_THANK_YOU = "Hey there! I really appreciate the tip. It's never expected, but anything goes towards making my content not suck!";

  const impersonating = JSON.parse(localStorage.getItem('impersonating') || 'null');

  const handleExitImpersonation = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_type');
    localStorage.removeItem('user');
    localStorage.removeItem('impersonating');
    localStorage.removeItem('admin_impersonate_backup');
    window.location.href = '/manage/users';
  };

  useEffect(() => {
    fetchUserProfile();
    fetchStripeData();
  }, []);

  const fetchStripeData = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    try {
      const [balanceRes, tipsRes, payoutsRes] = await Promise.all([
        fetch(`${config.API_URL}/creators/stripe-balance`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${config.API_URL}/creators/tips?limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${config.API_URL}/creators/payouts-history?limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (balanceRes.ok) {
        const balanceData = await balanceRes.json();
        setBalance(balanceData);
      }
      if (tipsRes.ok) {
        const tipsData = await tipsRes.json();
        setTips(tipsData.tips || []);
      }
      if (payoutsRes.ok) {
        const payoutsData = await payoutsRes.json();
        setPayouts(payoutsData.payouts || []);
      }
    } catch (err) {
      console.error('Error fetching Stripe data:', err);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/creator-login');
        return;
      }

      const userResponse = await fetch(`${config.API_URL}/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (userResponse.status === 401) {
        localStorage.clear();
        navigate('/creator-login');
        return;
      }

      if (userResponse.ok) {
        const userData = await userResponse.json();
        const linksResponse = await fetch(`${config.API_URL}/users/${userData.id}/links`);
        const linksData = linksResponse.ok ? await linksResponse.json() : [];
        setUser({ ...userData, links: linksData });
        setThankYouMessage(userData.thank_you_message || '');
      } else {
        throw new Error('Failed to fetch user profile');
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/creator-login');
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const token = localStorage.getItem('access_token');
    if (!token) return;

    setAvatarError('');
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const res = await fetch(`${config.API_URL}/creators/avatar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json();
      if (res.ok) {
        setUser((prev) => ({ ...prev, logo_url: data.logo_url }));
      } else {
        setAvatarError(typeof data?.detail === 'string' ? data.detail : 'Failed to update avatar.');
      }
    } catch (err) {
      console.error('Error uploading avatar:', err);
      setAvatarError('Something went wrong. Please try again.');
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const saveThankYouMessage = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    setSavingThankYou(true);
    try {
      const res = await fetch(`${config.API_URL}/creators/thank-you-message`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: thankYouMessage.trim() || null })
      });

      if (res.ok) {
        setEditingThankYou(false);
        setUser(prev => ({ ...prev, thank_you_message: thankYouMessage.trim() || null }));
      }
    } catch (err) {
      console.error('Error saving thank you message:', err);
    } finally {
      setSavingThankYou(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      succeeded: 'bg-emerald-500/20 text-emerald-400',
      pending: 'bg-yellow-500/20 text-yellow-400',
      processing: 'bg-cyan-500/20 text-cyan-400',
      requires_payment_method: 'bg-red-500/20 text-red-400',
      requires_confirmation: 'bg-yellow-500/20 text-yellow-400',
      canceled: 'bg-gray-500/20 text-gray-400',
    };
    return styles[status] || 'bg-gray-500/20 text-gray-400';
  };

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: FaHome },
    { id: 'castle', label: 'My Castle', icon: FaChessRook },
    { id: 'overlays', label: 'Overlays', icon: FaLayerGroup },
    { id: 'links', label: 'Profile Links', icon: FaLink },
    { id: 'reports', label: 'Reports', icon: FaChartBar, href: '/creator-donations' },
    { id: 'account', label: 'Account', icon: FaUser },
    { id: 'settings', label: 'Settings', icon: FaCog },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#14161f] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#14161f] text-white flex items-center justify-center">
        <div className="bg-[#1c1f2d] p-6 rounded-xl border border-red-500/50">
          <h2 className="text-xl font-bold mb-4">Error</h2>
          <p className="text-gray-400">{error}</p>
          <button onClick={() => window.location.reload()} className="mt-4 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold py-2 px-4 rounded-lg transition">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#14161f] text-white flex items-center justify-center">
        <p>No user data available</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#14161f] text-white flex">
      {/* Impersonation Banner */}
      {impersonating && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
          background: '#c4a240', padding: '0.5rem 1rem',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem',
          fontSize: '0.875rem', fontWeight: 600,
        }}>
          <span>Admin viewing as: {impersonating.name || impersonating.email} (@{impersonating.username})</span>
          <button
            onClick={handleExitImpersonation}
            style={{
              background: '#fff', color: '#c4a240', border: 'none', borderRadius: '0.375rem',
              padding: '0.25rem 0.75rem', fontWeight: 700, cursor: 'pointer',
            }}
          >
            Exit Impersonation
          </button>
        </div>
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full bg-[#0e1020] border-r border-gray-800/50 flex flex-col transition-all duration-300 z-40 ${sidebarCollapsed ? 'w-16' : 'w-56'} ${impersonating ? 'pt-10' : ''}`}>
        {/* Logo */}
        <div className="p-4 border-b border-gray-800/50">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-black font-bold text-sm">C</span>
            </div>
            {!sidebarCollapsed && (
              <div>
                <h1 className="text-sm font-bold text-white leading-tight">CASTLE</h1>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Creator Studio</p>
              </div>
            )}
          </div>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 py-4 px-2 space-y-1">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;

            if (item.href) {
              return (
                <Link
                  key={item.id}
                  to={item.href}
                  className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                    isActive
                      ? 'bg-cyan-500/10 text-cyan-400'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="flex-shrink-0" size={16} />
                  {!sidebarCollapsed && <span className="text-sm">{item.label}</span>}
                </Link>
              );
            }

            return (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                  isActive
                    ? 'bg-cyan-500/10 text-cyan-400'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon className="flex-shrink-0" size={16} />
                {!sidebarCollapsed && <span className="text-sm">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-2 border-t border-gray-800/50">
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/5 transition-all"
          >
            <FaSignOutAlt className="flex-shrink-0" size={16} />
            {!sidebarCollapsed && <span className="text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-56'} ${impersonating ? 'pt-10' : ''}`}>
        <div className="p-8 max-w-6xl">

          {/* Dashboard Section */}
          {activeSection === 'dashboard' && (
            <>
              {/* Header */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold">Dashboard</h2>
                <p className="text-gray-500 text-sm">Overview of your creator account</p>
              </div>

              {/* Tip Page CTA Banner */}
              <div className="bg-gradient-to-r from-cyan-900/30 to-[#1c1f2d] border border-cyan-800/30 rounded-xl p-5 mb-6 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold">Share your {terms.Donation} page</h3>
                  <p className="text-gray-400 text-sm mt-1">
                    <span className="text-cyan-400 font-mono">{window.location.origin}/{user.username}</span>
                  </p>
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(`${window.location.origin}/${user.username}`)}
                  className="flex items-center space-x-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold py-2.5 px-5 rounded-lg transition flex-shrink-0"
                >
                  <FaCopy size={14} />
                  <span>Copy Link</span>
                </button>
              </div>

              {/* Stat Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                <div className="bg-[#1c1f2d] rounded-xl p-5 border border-gray-800/50">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-gray-400 text-sm">Total {terms.Donations}</p>
                    <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                      <FaDollarSign className="text-cyan-400" size={14} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold">{balance ? formatCurrency(balance.total_tips) : '--'}</p>
                  <p className="text-xs text-gray-500 mt-1">{balance ? `${balance.tip_count} ${terms.donations}` : 'Loading...'}</p>
                </div>

                <div className="bg-[#1c1f2d] rounded-xl p-5 border border-gray-800/50">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-gray-400 text-sm">Paid Out</p>
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <FaMoneyBillWave className="text-blue-400" size={14} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-blue-400">{balance ? formatCurrency(balance.total_paid_out) : '--'}</p>
                  <p className="text-xs text-gray-500 mt-1">{balance ? `${balance.payout_count} payouts` : 'Loading...'}</p>
                </div>

                <div className="bg-[#1c1f2d] rounded-xl p-5 border border-cyan-500/30">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-gray-400 text-sm">Available</p>
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <FaWallet className="text-emerald-400" size={14} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-emerald-400">{balance ? formatCurrency(balance.net_available) : '--'}</p>
                  <p className="text-xs text-gray-500 mt-1">Ready for payout</p>
                </div>
              </div>

              {/* Recent Tips & Thank You - two columns */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Tips */}
                <div className="bg-[#1c1f2d] rounded-xl p-6 border border-gray-800/50">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold">Recent {terms.Donations}</h3>
                    <Link to="/creator-donations" className="text-xs text-cyan-400 hover:text-cyan-300 transition">
                      View All &rarr;
                    </Link>
                  </div>
                  {tips.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-6">No {terms.donations} yet</p>
                  ) : (
                    <div className="space-y-3">
                      {tips.slice(0, 5).map((tip) => (
                        <div key={tip.id} className="flex items-center justify-between py-2 border-b border-gray-800/50 last:border-0">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-full bg-cyan-500/10 flex items-center justify-center">
                              <FaUser className="text-cyan-400" size={10} />
                            </div>
                            <div>
                              <p className="text-sm font-medium">{tip.donor_name}</p>
                              <p className="text-xs text-gray-500">{formatDate(tip.created_at)}</p>
                            </div>
                          </div>
                          <span className="text-emerald-400 font-semibold text-sm">{formatCurrency(tip.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Thank You Message */}
                <div className="bg-[#1c1f2d] rounded-xl p-6 border border-gray-800/50">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold">Thank You Message</h3>
                    {!editingThankYou && (
                      <button
                        onClick={() => setEditingThankYou(true)}
                        className="text-xs text-cyan-400 hover:text-cyan-300 transition"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  <p className="text-gray-500 text-xs mb-3">
                    Shown to supporters after they send a {terms.donation}.
                  </p>

                  {editingThankYou ? (
                    <div>
                      <textarea
                        value={thankYouMessage}
                        onChange={(e) => setThankYouMessage(e.target.value)}
                        placeholder={DEFAULT_THANK_YOU}
                        maxLength={1000}
                        rows={4}
                        className="w-full px-4 py-3 bg-[#14161f] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none resize-none text-sm"
                      />
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-gray-600 text-xs">{thankYouMessage.length}/1000</span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => { setThankYouMessage(user.thank_you_message || ''); setEditingThankYou(false); }}
                            className="px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 rounded-lg transition"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={saveThankYouMessage}
                            disabled={savingThankYou}
                            className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded-lg transition"
                          >
                            {savingThankYou ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-[#14161f] p-4 rounded-lg">
                      <p className="text-gray-300 text-sm whitespace-pre-line">
                        {user.thank_you_message || <span className="text-gray-600 italic">{DEFAULT_THANK_YOU} (default)</span>}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Full Tips & Payouts Table */}
              <div className="mt-6 bg-[#1c1f2d] rounded-xl p-6 border border-gray-800/50">
                <div className="flex space-x-2 mb-4">
                  <button
                    onClick={() => setActiveTab('tips')}
                    className={`px-4 py-2 rounded-lg text-sm transition ${
                      activeTab === 'tips'
                        ? 'bg-cyan-500/10 text-cyan-400'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {terms.Donations} ({tips.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('payouts')}
                    className={`px-4 py-2 rounded-lg text-sm transition ${
                      activeTab === 'payouts'
                        ? 'bg-cyan-500/10 text-cyan-400'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    Payouts ({payouts.length})
                  </button>
                </div>

                {activeTab === 'tips' && (
                  <div className="overflow-x-auto">
                    {tips.length === 0 ? (
                      <p className="text-gray-500 text-center py-8 text-sm">No {terms.donations} yet</p>
                    ) : (
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-gray-500 text-xs uppercase tracking-wider border-b border-gray-800/50">
                            <th className="pb-3 font-medium">From</th>
                            <th className="pb-3 font-medium">Amount</th>
                            <th className="pb-3 font-medium">Status</th>
                            <th className="pb-3 font-medium">Message</th>
                            <th className="pb-3 font-medium">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/50">
                          {tips.map((tip) => (
                            <tr key={tip.id} className="hover:bg-white/[0.02]">
                              <td className="py-3 text-sm font-medium">{tip.donor_name}</td>
                              <td className="py-3 text-sm text-emerald-400">{formatCurrency(tip.amount)}</td>
                              <td className="py-3">
                                <span className={`px-2 py-1 rounded text-xs ${getStatusBadge(tip.status)}`}>
                                  {tip.status}
                                </span>
                              </td>
                              <td className="py-3 text-gray-400 text-sm max-w-xs truncate">{tip.message || '-'}</td>
                              <td className="py-3 text-gray-500 text-xs">{formatDate(tip.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {activeTab === 'payouts' && (
                  <div className="overflow-x-auto">
                    {payouts.length === 0 ? (
                      <p className="text-gray-500 text-center py-8 text-sm">No payouts yet</p>
                    ) : (
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-gray-500 text-xs uppercase tracking-wider border-b border-gray-800/50">
                            <th className="pb-3 font-medium">Amount</th>
                            <th className="pb-3 font-medium">Description</th>
                            <th className="pb-3 font-medium">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/50">
                          {payouts.map((payout) => (
                            <tr key={payout.id} className="hover:bg-white/[0.02]">
                              <td className="py-3 text-sm text-blue-400 font-medium">{formatCurrency(payout.amount)}</td>
                              <td className="py-3 text-sm text-gray-400">{payout.description || '-'}</td>
                              <td className="py-3 text-gray-500 text-xs">{formatDate(payout.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Profile Links Section */}
          {activeSection === 'links' && (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold">Profile Links</h2>
                <p className="text-gray-500 text-sm">Manage links shown on your {terms.donation} page</p>
              </div>
              <div className="bg-[#1c1f2d] rounded-xl p-6 border border-gray-800/50">
                <ProfileLinksManager userId={user.id} />
              </div>
            </>
          )}

          {/* Account Section */}
          {activeSection === 'account' && (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold">Account</h2>
                <p className="text-gray-500 text-sm">Your profile information</p>
              </div>
              <div className="bg-[#1c1f2d] rounded-xl p-6 border border-gray-800/50 mb-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-800 border border-gray-700 flex-shrink-0 flex items-center justify-center">
                    {user.logo_url ? (
                      <img src={user.logo_url} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <FaUser className="text-gray-600" size={24} />
                    )}
                  </div>
                  <div>
                    <button
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={avatarUploading}
                      className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded-lg transition"
                    >
                      {avatarUploading ? 'Uploading…' : 'Change Avatar'}
                    </button>
                    <p className="text-gray-600 text-xs mt-1.5">JPG, PNG, GIF, or WebP. Max 5MB.</p>
                    {avatarError && <p className="text-red-400 text-xs mt-1">{avatarError}</p>}
                  </div>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp,image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                </div>
              </div>

              <div className="bg-[#1c1f2d] rounded-xl p-6 border border-gray-800/50">
                <div className="space-y-4">
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider">Name</p>
                    <p className="text-white font-medium mt-1">{user.first_name} {user.last_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider">Email</p>
                    <p className="text-white font-medium mt-1">{user.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider">Username</p>
                    <p className="text-white font-medium mt-1">@{user.username}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider">User ID</p>
                    <p className="text-gray-400 font-mono text-sm mt-1">{user.id}</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Settings Section */}
          {activeSection === 'settings' && (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold">Settings</h2>
                <p className="text-gray-500 text-sm">Configure your creator preferences</p>
              </div>

              {/* Thank You Message */}
              <div className="bg-[#1c1f2d] rounded-xl p-6 border border-gray-800/50 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Thank You Message</h3>
                  {!editingThankYou && (
                    <button
                      onClick={() => setEditingThankYou(true)}
                      className="text-xs text-cyan-400 hover:text-cyan-300 transition"
                    >
                      Edit
                    </button>
                  )}
                </div>
                <p className="text-gray-500 text-xs mb-3">
                  This message is shown to supporters after they send a {terms.donation}.
                </p>

                {editingThankYou ? (
                  <div>
                    <textarea
                      value={thankYouMessage}
                      onChange={(e) => setThankYouMessage(e.target.value)}
                      placeholder={DEFAULT_THANK_YOU}
                      maxLength={1000}
                      rows={4}
                      className="w-full px-4 py-3 bg-[#14161f] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none resize-none text-sm"
                    />
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-gray-600 text-xs">{thankYouMessage.length}/1000</span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => { setThankYouMessage(user.thank_you_message || ''); setEditingThankYou(false); }}
                          className="px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 rounded-lg transition"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={saveThankYouMessage}
                          disabled={savingThankYou}
                          className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded-lg transition"
                        >
                          {savingThankYou ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#14161f] p-4 rounded-lg">
                    <p className="text-gray-300 text-sm whitespace-pre-line">
                      {user.thank_you_message || <span className="text-gray-600 italic">{DEFAULT_THANK_YOU} (default)</span>}
                    </p>
                  </div>
                )}
              </div>

              {/* Tip Page Link */}
              <div className="bg-[#1c1f2d] rounded-xl p-6 border border-gray-800/50">
                <h3 className="font-semibold mb-4">Your {terms.Donation} Page</h3>
                <div className="bg-[#14161f] p-3 rounded-lg">
                  <p className="text-cyan-400 font-mono text-sm break-all">{window.location.origin}/{user.username}</p>
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(`${window.location.origin}/${user.username}`)}
                  className="mt-3 flex items-center space-x-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold py-2 px-4 rounded-lg transition text-sm"
                >
                  <FaCopy size={12} />
                  <span>Copy Link</span>
                </button>
              </div>
            </>
          )}

          {/* My Castle Section */}
          {activeSection === 'castle' && (
            <MyCastleSection user={user} />
          )}

          {activeSection === 'overlays' && (
            <OverlaysSection username={user.username} />
          )}

        </div>
      </main>
    </div>
  );
}

function OverlaysSection({ username }) {
  const navigate = useNavigate();
  const toSlug = (str) =>
    str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'scene';
  const [copied, setCopied] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [loadingScenes, setLoadingScenes] = useState(true);
  const [showNewSceneModal, setShowNewSceneModal] = useState(false);
  const [newSceneName, setNewSceneName] = useState('Untitled scene');
  const [selectedSize, setSelectedSize] = useState('16:9');
  const [customWidth, setCustomWidth] = useState(1920);
  const [customHeight, setCustomHeight] = useState(1080);
  const [creating, setCreating] = useState(false);

  const origin = window.location.origin;

  const SIZE_OPTIONS = [
    { key: '16:9', label: '16:9', sub: '1920 × 1080', width: 1920, height: 1080 },
    { key: '9:16', label: '9:16', sub: '1080 × 1920', width: 1080, height: 1920 },
    { key: '4:3', label: '4:3', sub: '1440 × 1080', width: 1440, height: 1080 },
    { key: '1:1', label: '1:1', sub: '1080 × 1080', width: 1080, height: 1080 },
    { key: '21:9', label: '21:9', sub: '2560 × 1080', width: 2560, height: 1080 },
    { key: 'custom', label: 'Custom', sub: 'Set your own', width: null, height: null },
  ];

  const builtInOverlays = [
    {
      id: 'recent-tips',
      name: 'Recent Tips',
      description: 'Scrolling ticker of your latest supporters',
      dimensions: '1920 × 1080',
      url: `${origin}/overlay/tips/${username}`,
    },
    {
      id: 'tip-alert',
      name: 'Tip Alert',
      description: 'Animated popup when a new tip comes in',
      dimensions: '1920 × 1080',
      url: `${origin}/overlay/tipalert/${username}`,
    },
  ];

  useEffect(() => {
    const fetchScenes = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      try {
        const res = await fetch(`${config.API_URL}/overlay-scenes/`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setScenes(data);
        }
      } catch (err) {
        console.error('Error fetching scenes:', err);
      } finally {
        setLoadingScenes(false);
      }
    };
    fetchScenes();
  }, []);

  const copyUrl = (id, url) => {
    navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const openNewSceneModal = () => {
    setNewSceneName('Untitled scene');
    setSelectedSize('16:9');
    setCustomWidth(1920);
    setCustomHeight(1080);
    setShowNewSceneModal(true);
  };

  const createScene = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    const sizeOption = SIZE_OPTIONS.find(o => o.key === selectedSize);
    const width = selectedSize === 'custom' ? customWidth : sizeOption.width;
    const height = selectedSize === 'custom' ? customHeight : sizeOption.height;
    setCreating(true);
    try {
      const res = await fetch(`${config.API_URL}/overlay-scenes/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newSceneName || 'Untitled scene', width, height, background: 'transparent' }),
      });
      if (res.ok) {
        const scene = await res.json();
        setShowNewSceneModal(false);
        navigate(`/overlay/editor/${scene.id}`);
      }
    } catch (err) {
      console.error('Error creating scene:', err);
    } finally {
      setCreating(false);
    }
  };

  const checkerboardStyle = {
    backgroundImage: 'linear-gradient(45deg, #14161f 25%, transparent 25%), linear-gradient(-45deg, #14161f 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #14161f 75%), linear-gradient(-45deg, transparent 75%, #14161f 75%)',
    backgroundSize: '16px 16px',
    backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
    backgroundColor: '#14161f',
  };

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold">Overlays</h2>
          <p className="text-gray-500 text-sm">Paste a URL into OBS as a browser source.</p>
        </div>
        <button
          onClick={openNewSceneModal}
          className="flex items-center space-x-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold py-2 px-4 rounded-lg transition text-sm"
        >
          <span>+ New scene</span>
        </button>
      </div>

      {/* Built-in overlays */}
      <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Built-in overlays</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
        {builtInOverlays.map((overlay) => (
          <div key={overlay.id} className="bg-[#1c1f2d] border border-gray-800/50 rounded-xl overflow-hidden">
            <div className="w-full aspect-video" style={checkerboardStyle}>
              <div className="w-full h-full flex items-end p-2">
                <span className="text-[10px] font-mono text-gray-500 bg-black/60 px-1.5 py-0.5 rounded">
                  {overlay.dimensions}
                </span>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-white truncate">{overlay.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{overlay.description}</p>
                </div>
                <a
                  href={overlay.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-gray-300 transition flex-shrink-0 mt-0.5"
                  title="Open in new tab"
                >
                  <FaExternalLinkAlt size={12} />
                </a>
              </div>
              <div className="mt-3 flex items-center gap-2 bg-[#14161f] border border-gray-800 rounded-lg px-3 py-2">
                <span className="flex-1 text-xs text-gray-500 font-mono truncate">{overlay.url}</span>
                <button
                  onClick={() => copyUrl(overlay.id, overlay.url)}
                  className="flex-shrink-0 text-gray-500 hover:text-cyan-400 transition"
                  title="Copy URL"
                >
                  {copied === overlay.id ? <FaCheck size={12} className="text-green-400" /> : <FaCopy size={12} />}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Custom scenes */}
      <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Custom scenes</p>
      {loadingScenes ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-500"></div>
        </div>
      ) : scenes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-[#1c1f2d] border border-gray-800/50 rounded-xl">
          <FaLayerGroup size={32} className="text-gray-600 mb-4" />
          <p className="text-gray-400 font-medium mb-1">No scenes yet</p>
          <p className="text-gray-600 text-sm mb-5">Create a custom scene to compose your own overlay layout.</p>
          <button
            onClick={openNewSceneModal}
            className="flex items-center space-x-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold py-2 px-4 rounded-lg transition text-sm"
          >
            <span>+ New scene</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {scenes.map((scene) => {
            const sceneUrl = `${origin}/overlay/scene/${username}/${toSlug(scene.name)}/${scene.id}`;
            const dimLabel = `${scene.width} × ${scene.height}`;
            return (
              <div key={scene.id} className="bg-[#1c1f2d] border border-gray-800/50 rounded-xl overflow-hidden">
                <div className="w-full aspect-video" style={checkerboardStyle}>
                  <div className="w-full h-full flex items-end p-2">
                    <span className="text-[10px] font-mono text-gray-500 bg-black/60 px-1.5 py-0.5 rounded">
                      {dimLabel}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm text-white truncate">{scene.name}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <a
                        href={sceneUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-500 hover:text-gray-300 transition mt-0.5"
                        title="Open in new tab"
                      >
                        <FaExternalLinkAlt size={12} />
                      </a>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2 bg-[#14161f] border border-gray-800 rounded-lg px-3 py-2">
                    <span className="flex-1 text-xs text-gray-500 font-mono truncate">{sceneUrl}</span>
                    <button
                      onClick={() => copyUrl(scene.id, sceneUrl)}
                      className="flex-shrink-0 text-gray-500 hover:text-cyan-400 transition"
                      title="Copy URL"
                    >
                      {copied === scene.id ? <FaCheck size={12} className="text-green-400" /> : <FaCopy size={12} />}
                    </button>
                  </div>
                  <button
                    onClick={() => navigate(`/overlay/editor/${scene.id}`)}
                    className="mt-3 w-full flex items-center justify-center space-x-2 bg-[#14161f] hover:bg-[#252a3d] border border-gray-700 text-gray-300 hover:text-white font-medium py-2 px-3 rounded-lg transition text-sm"
                  >
                    <FaEdit size={12} />
                    <span>Edit</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Scene Modal */}
      {showNewSceneModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1c1f2d] border border-gray-700 rounded-2xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-bold mb-5">New scene</h3>

            {/* Name */}
            <div className="mb-5">
              <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">Scene name</label>
              <input
                type="text"
                value={newSceneName}
                onChange={(e) => setNewSceneName(e.target.value)}
                className="w-full px-3 py-2.5 bg-[#14161f] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none text-sm text-white"
                placeholder="Untitled scene"
              />
            </div>

            {/* Canvas size */}
            <div className="mb-6">
              <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">Canvas size</label>
              <div className="grid grid-cols-3 gap-2">
                {SIZE_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSelectedSize(opt.key)}
                    className={`flex flex-col items-center justify-center py-3 px-2 rounded-lg border text-center transition ${
                      selectedSize === opt.key
                        ? 'border-cyan-500 bg-cyan-500/10 text-cyan-400'
                        : 'border-gray-700 bg-[#14161f] text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <span className="text-sm font-semibold">{opt.label}</span>
                    <span className="text-[10px] mt-0.5 opacity-70">{opt.sub}</span>
                  </button>
                ))}
              </div>

              {selectedSize === 'custom' && (
                <div className="mt-3 flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Width (px)</label>
                    <input
                      type="number"
                      value={customWidth}
                      onChange={(e) => setCustomWidth(Number(e.target.value))}
                      min={100}
                      max={7680}
                      className="w-full px-3 py-2 bg-[#14161f] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none text-sm text-white"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Height (px)</label>
                    <input
                      type="number"
                      value={customHeight}
                      onChange={(e) => setCustomHeight(Number(e.target.value))}
                      min={100}
                      max={4320}
                      className="w-full px-3 py-2 bg-[#14161f] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none text-sm text-white"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowNewSceneModal(false)}
                className="px-4 py-2 text-sm bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={createScene}
                disabled={creating}
                className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-semibold rounded-lg transition"
              >
                {creating ? 'Creating...' : 'Create scene'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}